import type { PortalCustomizationChange } from "@redotech/admin-sdk/rpc/schema/team/copy-portal-customization";
import { useRequiredContext } from "@redotech/react-util/context";
import { RedoButton } from "@redotech/redo-web/arbiter-components/buttons/redo-button";
import { RedoModal } from "@redotech/redo-web/arbiter-components/modal/redo-modal";
import { Flex } from "@redotech/redo-web/flex";
import { Text } from "@redotech/redo-web/text";
import { memo, useCallback, useEffect, useState } from "react";
import { RedoAdminRpcClientContext } from "../../../app/redo-admin-rpc-client-provider";
import * as styles from "./copy-portal-customization.module.css";
import { TeamSearchBox } from "./team-search-box";

function downloadJson(filename: string, json: string): void {
  const blob = new Blob([json], { type: "application/json" });
  const url = URL.createObjectURL(blob);
  const a = document.createElement("a");
  a.href = url;
  a.download = filename;
  a.click();
  URL.revokeObjectURL(url);
}

/** Renders a diff value ("" = unset) as readable text for the monospace box. */
function boxValue(display: string): string {
  if (display === "") {
    return "(unset)";
  }
  try {
    const parsed = JSON.parse(display);
    return typeof parsed === "string"
      ? parsed
      : JSON.stringify(parsed, null, 2);
  } catch {
    return display;
  }
}

export const CopyPortalCustomizationSettings = memo(
  function CopyPortalCustomizationSettings({
    fixedSourceTeamId,
    fixedSourceTeamName,
  }: {
    /** When set, the source is this team and only a destination is picked. */
    fixedSourceTeamId?: string;
    fixedSourceTeamName?: string;
  }) {
    const rpcClient = useRequiredContext(RedoAdminRpcClientContext);
    const [pickedSourceTeamId, setPickedSourceTeamId] = useState("");
    const [destinationTeamId, setDestinationTeamId] = useState("");
    const [confirmOpen, setConfirmOpen] = useState(false);
    const [pending, setPending] = useState(false);
    const [preview, setPreview] = useState<{
      changes: PortalCustomizationChange[];
      sharedAssetUrls: string[];
      backup?: string;
      sourceTeamName?: string;
      destinationTeamName?: string;
    } | null>(null);
    const [result, setResult] = useState<{
      success: boolean;
      message: string;
    } | null>(null);

    const sourceTeamId = fixedSourceTeamId ?? pickedSourceTeamId;
    const backupFilename = `team-${destinationTeamId}-portal-customization-backup.json`;

    const ready = Boolean(
      sourceTeamId && destinationTeamId && sourceTeamId !== destinationTeamId,
    );

    // Any change to the team selection invalidates a prior preview so the
    // confirm dialog and Apply can never act on a diff nobody reviewed.
    useEffect(() => {
      setPreview(null);
      setResult(null);
      setConfirmOpen(false);
    }, [sourceTeamId, destinationTeamId]);

    const handlePreview = useCallback(async () => {
      setPending(true);
      setResult(null);
      setPreview(null);
      try {
        const response = await rpcClient.copyPortalCustomization({
          sourceTeamId,
          destinationTeamId,
          dryRun: true,
        });
        if (!response.success) {
          setResult({ success: false, message: response.message });
          return;
        }
        setPreview(response);
      } catch (error) {
        setResult({
          success: false,
          message:
            error instanceof Error ? error.message : "Unknown error occurred",
        });
      } finally {
        setPending(false);
      }
    }, [rpcClient, sourceTeamId, destinationTeamId]);

    const handleApply = useCallback(async () => {
      setConfirmOpen(false);
      setPending(true);
      setResult(null);
      try {
        const response = await rpcClient.copyPortalCustomization({
          sourceTeamId,
          destinationTeamId,
          dryRun: false,
        });
        // Always hand the operator the pre-change snapshot so they can revert.
        if (response.backup) {
          downloadJson(backupFilename, response.backup);
        }
        setResult({ success: response.success, message: response.message });
        if (response.success) {
          setPreview(null);
        }
      } catch (error) {
        setResult({
          success: false,
          message:
            error instanceof Error ? error.message : "Unknown error occurred",
        });
      } finally {
        setPending(false);
      }
    }, [rpcClient, sourceTeamId, destinationTeamId, backupFilename]);

    return (
      <Flex dir="column" gap="lg">
        <Text fontSize="xs" textColor="secondary">
          Copies Portal Builder styling
          {fixedSourceTeamName ? ` from ${fixedSourceTeamName}` : ""} to another
          team: branding, colors, navbar, typography, buttons, input fields, and
          per-page text. Useful when a merchant runs multiple stores and wants
          the same portal look without rebuilding it by hand. Custom font
          families the styling references are copied into the destination&apos;s
          brand kit so the fonts actually render. Does NOT copy the portal
          domain or path prefix, legacy portal CSS, product/collection
          exclusions, or warranty and bulk-return settings. Preview first, then
          apply — a backup of the destination&apos;s current customization is
          downloaded automatically on apply.
        </Text>
        {!fixedSourceTeamId && (
          <TeamSearchBox
            label="Source team (copy portal styling from)"
            setValue={setPickedSourceTeamId}
            showSelectedCard
            value={pickedSourceTeamId}
          />
        )}
        <TeamSearchBox
          label="Destination team (apply portal styling to)"
          setValue={setDestinationTeamId}
          showSelectedCard
          value={destinationTeamId}
        />
        <Flex>
          <RedoButton
            disabled={!ready}
            hierarchy="secondary"
            onClick={handlePreview}
            pending={pending && !preview}
            size="sm"
            text="Preview changes"
          />
        </Flex>
        {preview && (
          <Flex dir="column" gap="md">
            <Text fontSize="sm" fontWeight="semibold">
              {preview.changes.length === 0
                ? "No differences — destination portal already matches source"
                : `${preview.changes.length} section(s) will change`}
            </Text>
            {preview.sharedAssetUrls.length > 0 && (
              <Flex
                className={`${styles.result} ${styles.notice}`}
                dir="column"
                gap="xs"
              >
                <Text fontSize="xs" fontWeight="semibold">
                  {preview.sharedAssetUrls.length} asset(s) stay pointed at the
                  source team&apos;s uploads
                </Text>
                <Text fontSize="xs">
                  Images and font files are referenced by URL, not re-uploaded.
                  They render fine on the destination portal; re-upload there if
                  it needs its own artwork.
                </Text>
              </Flex>
            )}
            {preview.changes.length > 0 && (
              <Flex className={styles.changeList} dir="column" gap="sm">
                {preview.changes.map((change) => (
                  <div className={styles.change} key={change.path}>
                    <div className={styles.changePath}>{change.path}</div>
                    <div className={styles.diffRow}>
                      <textarea
                        className={styles.diffValue}
                        readOnly
                        value={boxValue(change.before)}
                      />
                      <span className={styles.diffArrow}>→</span>
                      <textarea
                        className={styles.diffValue}
                        readOnly
                        value={boxValue(change.after)}
                      />
                    </div>
                  </div>
                ))}
              </Flex>
            )}
            <Flex gap="sm">
              {preview.backup && (
                <RedoButton
                  hierarchy="tertiary"
                  onClick={() =>
                    downloadJson(backupFilename, preview.backup ?? "")
                  }
                  size="sm"
                  text="Download destination backup"
                />
              )}
              {preview.changes.length > 0 && (
                <RedoButton
                  hierarchy="primary"
                  onClick={() => setConfirmOpen(true)}
                  pending={pending}
                  size="sm"
                  text="Apply changes"
                />
              )}
            </Flex>
          </Flex>
        )}
        {result && (
          <Flex
            className={`${styles.result} ${result.success ? styles.success : styles.error}`}
            dir="column"
          >
            <Text fontSize="xs">{result.message}</Text>
          </Flex>
        )}
        <RedoModal
          isOpen={confirmOpen}
          modalSize="sm"
          onModalCloseRequested={() => setConfirmOpen(false)}
          primaryButton={{ text: "Apply", onClick: handleApply }}
          secondaryButton={{
            text: "Cancel",
            onClick: () => setConfirmOpen(false),
          }}
          theme="warn"
          title="Confirm copy portal customization"
        >
          <Text fontSize="xs">
            This will replace the Portal Builder styling on{" "}
            <Text as="span" fontSize="xs" fontWeight="bold">
              {preview?.destinationTeamName ?? destinationTeamId}
            </Text>
            . A backup of its current customization will be downloaded first.
            Are you sure?
          </Text>
        </RedoModal>
      </Flex>
    );
  },
);
